import { apiClient } from '../../../core/config/api';
import type {
  ApproveRequest,
  EventCreate,
  EventOccurrenceData,
  EventUpdate,
  GoalLinkWrite,
  MomentumData,
  NextUpData,
  OwnTaskCreate,
  OwnTaskUpdate,
  PlanRange,
  PlanViewData,
  PlanningSettingsData,
  PlanningSettingsUpdate,
  RunningTimerData,
  TaskCardData,
  TaskDetailsData,
  TaskPlanWrite,
  TaskPrivateUpdate,
} from '../types/planner';

const BASE = '/planner/me';

export const plannerApi = {
  getPlan: async (range?: PlanRange): Promise<PlanViewData> => {
    const response = await apiClient.get<PlanViewData>(`${BASE}/plan`, { params: range });
    return response.data;
  },

  approve: async (data: ApproveRequest): Promise<TaskCardData[]> => {
    const response = await apiClient.post<TaskCardData[]>(`${BASE}/plan/approve`, data);
    return response.data;
  },

  getNotPlanned: async (): Promise<TaskCardData[]> => {
    const response = await apiClient.get<TaskCardData[]>(`${BASE}/not-planned`);
    return response.data;
  },

  getNextUp: async (classId: string): Promise<NextUpData | null> => {
    const response = await apiClient.get<NextUpData | null>(`${BASE}/next-up`, { params: { class_id: classId } });
    return response.data;
  },

  getClassTasks: async (classId: string): Promise<TaskCardData[]> => {
    const response = await apiClient.get<TaskCardData[]>(`${BASE}/class-tasks`, { params: { class_id: classId } });
    return response.data;
  },

  getAssignment: async (studentAssignmentId: string): Promise<TaskDetailsData> => {
    const response = await apiClient.get<TaskDetailsData>(`${BASE}/assignments/${studentAssignmentId}`);
    return response.data;
  },

  planAssignment: async (studentAssignmentId: string, data: TaskPlanWrite): Promise<TaskDetailsData> => {
    const response = await apiClient.put<TaskDetailsData>(`${BASE}/assignments/${studentAssignmentId}/plan`, data);
    return response.data;
  },

  updateAssignmentPrivate: async (studentAssignmentId: string, data: TaskPrivateUpdate): Promise<TaskDetailsData> => {
    const response = await apiClient.patch<TaskDetailsData>(`${BASE}/assignments/${studentAssignmentId}/private`, data);
    return response.data;
  },

  setAssignmentGoal: async (studentAssignmentId: string, data: GoalLinkWrite): Promise<TaskDetailsData> => {
    const response = await apiClient.put<TaskDetailsData>(`${BASE}/assignments/${studentAssignmentId}/goal`, data);
    return response.data;
  },

  createTask: async (data: OwnTaskCreate): Promise<TaskCardData> => {
    const response = await apiClient.post<TaskCardData>(`${BASE}/tasks`, data);
    return response.data;
  },

  getTask: async (taskId: string): Promise<TaskDetailsData> => {
    const response = await apiClient.get<TaskDetailsData>(`${BASE}/tasks/${taskId}`);
    return response.data;
  },

  updateTask: async (taskId: string, data: OwnTaskUpdate): Promise<TaskDetailsData> => {
    const response = await apiClient.patch<TaskDetailsData>(`${BASE}/tasks/${taskId}`, data);
    return response.data;
  },

  deleteTask: async (taskId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/tasks/${taskId}`);
  },

  markDone: async (taskId: string): Promise<TaskDetailsData> => {
    const response = await apiClient.put<TaskDetailsData>(`${BASE}/tasks/${taskId}/done`);
    return response.data;
  },

  markNotDone: async (taskId: string): Promise<TaskDetailsData> => {
    const response = await apiClient.delete<TaskDetailsData>(`${BASE}/tasks/${taskId}/done`);
    return response.data;
  },

  planTask: async (taskId: string, data: TaskPlanWrite): Promise<TaskDetailsData> => {
    const response = await apiClient.put<TaskDetailsData>(`${BASE}/tasks/${taskId}/plan`, data);
    return response.data;
  },

  updateTaskPrivate: async (taskId: string, data: TaskPrivateUpdate): Promise<TaskDetailsData> => {
    const response = await apiClient.patch<TaskDetailsData>(`${BASE}/tasks/${taskId}/private`, data);
    return response.data;
  },

  startTimer: async (taskId: string): Promise<TaskDetailsData> => {
    const response = await apiClient.post<TaskDetailsData>(`${BASE}/tasks/${taskId}/timer/start`);
    return response.data;
  },

  stopTimer: async (taskId: string): Promise<TaskDetailsData> => {
    const response = await apiClient.post<TaskDetailsData>(`${BASE}/tasks/${taskId}/timer/stop`);
    return response.data;
  },

  timerSeen: async (taskId: string): Promise<void> => {
    await apiClient.post(`${BASE}/tasks/${taskId}/timer/seen`);
  },

  getRunningTimer: async (): Promise<RunningTimerData | null> => {
    const response = await apiClient.get<RunningTimerData | null>(`${BASE}/timer`);
    return response.data;
  },

  getEvents: async (range: PlanRange): Promise<EventOccurrenceData[]> => {
    const response = await apiClient.get<EventOccurrenceData[]>(`${BASE}/events`, { params: range });
    return response.data;
  },

  createEvent: async (data: EventCreate): Promise<void> => {
    await apiClient.post(`${BASE}/events`, data);
  },

  updateEvent: async (eventId: string, data: EventUpdate): Promise<void> => {
    await apiClient.patch(`${BASE}/events/${eventId}`, data);
  },

  deleteEvent: async (eventId: string, occurrenceDate?: string): Promise<void> => {
    const params = occurrenceDate ? { occurrence_date: occurrenceDate } : undefined;
    await apiClient.delete(`${BASE}/events/${eventId}`, { params });
  },

  getSettings: async (): Promise<PlanningSettingsData> => {
    const response = await apiClient.get<PlanningSettingsData>(`${BASE}/settings`);
    return response.data;
  },

  updateSettings: async (data: PlanningSettingsUpdate): Promise<PlanningSettingsData> => {
    const response = await apiClient.patch<PlanningSettingsData>(`${BASE}/settings`, data);
    return response.data;
  },

  getMomentum: async (): Promise<MomentumData> => {
    const response = await apiClient.get<MomentumData>(`${BASE}/momentum`);
    return response.data;
  },
};
